import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ClientProvider } from "./contexts/ClientContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import CookieConsentBanner from "./components/CookieConsentBanner";

const ChatwootWidget = lazy(() => import("./components/ChatwootWidget"));

// Routes where the public chat widget should NOT appear:
// auth flows + the logged-in app (those have their own UI).
const CHAT_HIDE_PREFIXES = [
  "/login", "/register", "/signup", "/verify-email",
  "/admin", "/dashboard",
  "/keywords", "/content", "/cms", "/outreach", "/reporting", "/reporting-legacy",
  "/audit", "/brand-intel", "/brand-mentions", "/reddit", "/calendar",
  "/ai-visibility", "/decay", "/cannibalization",
  "/settings",
];

function GlobalChatWidget() {
  const { pathname } = useLocation();
  if (CHAT_HIDE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"))) {
    return null;
  }
  return <Suspense fallback={null}><ChatwootWidget /></Suspense>;
}

// ───────────────────────────────────────────────────────────────
// Eagerly load: public pages most visitors see (homepage, blog).
// Lazy-load: everything behind auth + heavy components.
//
// This cuts the initial bundle from ~500KB → ~150KB gzipped,
// drastically improving first-paint on the homepage.
// ───────────────────────────────────────────────────────────────

// Eagerly loaded — always in main bundle
import HomePage from "./pages/HomePage";

// Lazily loaded — each becomes its own chunk, downloaded only when route hits
const LoginPage = lazy(() => import("./pages/LoginPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const BlogsPage = lazy(() => import("./pages/BlogsPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
// Programmatic SEO — one component renders all 10K-page matrix entries by slug
const UseCasePage = lazy(() => import("./pages/UseCasePage"));
const UseCasesIndexPage = lazy(() => import("./pages/UseCasesIndexPage"));
const HubPage = lazy(() => import("./pages/HubPage"));
const AffiliatesPage = lazy(() => import("./pages/AffiliatesPage"));
const SaasAffiliateProgramPage = lazy(() => import("./pages/SaasAffiliateProgramPage"));
const AffiliateDashboardPage = lazy(() => import("./pages/AffiliateDashboardPage"));
const AffiliateResourcesPage = lazy(() => import("./pages/AffiliateResourcesPage"));
const AffiliateTermsPage = lazy(() => import("./pages/AffiliateTermsPage"));
const AffiliateForSeoAgenciesPage = lazy(() => import("./pages/affiliate-program/for/ForSeoAgenciesPage"));
const AffiliateForContentCreatorsPage = lazy(() => import("./pages/affiliate-program/for/ForContentCreatorsPage"));
const AffiliateForIndiaPage = lazy(() => import("./pages/affiliate-program/for/ForIndiaPage"));
const AffiliateEarningsCalculatorPage = lazy(() => import("./pages/affiliate-program/EarningsCalculatorPage"));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage"));
const ChromeExtensionPage = lazy(() => import("./pages/ChromeExtensionPage"));
const ShopifyAppPage = lazy(() => import("./pages/ShopifyAppPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage")); // pulls in PayPal SDK — heavy
const ThankYouPage = lazy(() => import("./pages/ThankYouPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const ForAgenciesPage = lazy(() => import("./pages/ForAgenciesPage"));
const GeoOptimizationPage = lazy(() => import("./pages/GeoOptimizationPage"));
const AiCitationTrackingPage = lazy(() => import("./pages/AiCitationTrackingPage"));

// Competitor alternative + category pages
const AhrefsAlternativePage = lazy(() => import("./pages/AhrefsAlternativePage"));
const SemrushAlternativePage = lazy(() => import("./pages/SemrushAlternativePage"));
const SurferSeoAlternativePage = lazy(() => import("./pages/SurferSeoAlternativePage"));
const AiSeoServicesPage = lazy(() => import("./pages/AiSeoServicesPage"));
const AiSeoToolsPage = lazy(() => import("./pages/AiSeoToolsPage"));
const KeywordClusteringPage = lazy(() => import("./pages/KeywordClusteringPage"));
const SeoAutomationPage = lazy(() => import("./pages/SeoAutomationPage"));
const TopicalAuthorityPage = lazy(() => import("./pages/TopicalAuthorityPage"));
const SearchIntentToolPage = lazy(() => import("./pages/SearchIntentToolPage"));

// Free tools
const AiVisibilityCheckerPage = lazy(() => import("./pages/AiVisibilityCheckerPage"));
const SchemaGeneratorPage = lazy(() => import("./pages/SchemaGeneratorPage"));
const MetaTagAnalyzerPage = lazy(() => import("./pages/MetaTagAnalyzerPage"));
const SitemapAnalyzerPage = lazy(() => import("./pages/SitemapAnalyzerPage"));
const AiContentDetectorPage = lazy(() => import("./pages/AiContentDetectorPage"));

// B2B sales pages (enterprise + agency program)
const AgencyProgramPage = lazy(() => import("./pages/AgencyProgramPage"));
const EnterprisePage = lazy(() => import("./pages/EnterprisePage"));

// Settings: white-label + API keys (Agency tier+)
const WhitelabelSettingsPage = lazy(() => import("./pages/WhitelabelSettingsPage"));
const ApiKeysPage = lazy(() => import("./pages/ApiKeysPage"));
const AiVisibilityPage = lazy(() => import("./pages/AiVisibilityPage"));
const ContentDecayPage = lazy(() => import("./pages/ContentDecayPage"));
const CannibalizationPage = lazy(() => import("./pages/CannibalizationPage"));

// Resources hub — single-page discovery for all marketing surfaces
const ResourcesHubPage = lazy(() => import("./pages/ResourcesHubPage"));

// Changelog — public timeline of customer-facing updates
const ChangelogPage = lazy(() => import("./pages/ChangelogPage"));

// /[tool]-alternative pages — different positioning from /vs (bottom-funnel)
const JasperAlternativePage = lazy(() => import("./pages/alternatives/JasperAlternativePage"));
const CopyAiAlternativePage = lazy(() => import("./pages/alternatives/CopyAiAlternativePage"));
const WritesonicAlternativePage = lazy(() => import("./pages/alternatives/WritesonicAlternativePage"));
const OutrankingAlternativePage = lazy(() => import("./pages/alternatives/OutrankingAlternativePage"));
const ScalenutAlternativePage = lazy(() => import("./pages/alternatives/ScalenutAlternativePage"));

// /vs head-to-head competitor comparison pages
const VsSurferSeoPage = lazy(() => import("./pages/vs/VsSurferSeoPage"));
const VsFrasePage = lazy(() => import("./pages/vs/VsFrasePage"));
const VsMarketmusePage = lazy(() => import("./pages/vs/VsMarketmusePage"));
const VsClearscopePage = lazy(() => import("./pages/vs/VsClearscopePage"));
const VsClearscopeVsSemrushPage = lazy(() => import("./pages/vs/VsClearscopeVsSemrushPage"));
const VsMarketmuseVsClearscopePage = lazy(() => import("./pages/vs/VsMarketmuseVsClearscopePage"));
const ClearscopeAlternativePage = lazy(() => import("./pages/ClearscopeAlternativePage"));
const MarketmuseAlternativePage = lazy(() => import("./pages/MarketmuseAlternativePage"));
const VsNeuronwriterPage = lazy(() => import("./pages/vs/VsNeuronwriterPage"));
const VsAhrefsPage = lazy(() => import("./pages/vs/VsAhrefsPage"));
const VsSemrushPage = lazy(() => import("./pages/vs/VsSemrushPage"));

// Industry landing pages — programmatic SEO matrix
const AiSeoForSaasPage = lazy(() => import("./pages/industries/AiSeoForSaasPage"));
const AiSeoForEcommercePage = lazy(() => import("./pages/industries/AiSeoForEcommercePage"));
const AiSeoForMarketingAgenciesPage = lazy(() => import("./pages/industries/AiSeoForMarketingAgenciesPage"));
const AiSeoForRealEstatePage = lazy(() => import("./pages/industries/AiSeoForRealEstatePage"));
const AiSeoForHealthcarePage = lazy(() => import("./pages/industries/AiSeoForHealthcarePage"));
const AiSeoForLawFirmsPage = lazy(() => import("./pages/industries/AiSeoForLawFirmsPage"));
const AiSeoForRestaurantsPage = lazy(() => import("./pages/industries/AiSeoForRestaurantsPage"));
const AiSeoForDentistsPage = lazy(() => import("./pages/industries/AiSeoForDentistsPage"));
const AiSeoForCoachesPage = lazy(() => import("./pages/industries/AiSeoForCoachesPage"));
const AiSeoForB2bPage = lazy(() => import("./pages/industries/AiSeoForB2bPage"));

// SEO/GEO pillar + child pages
const SeoGuidePage = lazy(() => import("./pages/SeoGuidePage"));
const GeoGuidePage = lazy(() => import("./pages/GeoGuidePage"));
const WhatIsSearchIntentPage = lazy(() => import("./pages/WhatIsSearchIntentPage"));
const SearchIntentKeywordResearchPage = lazy(() => import("./pages/SearchIntentKeywordResearchPage"));
const GeoVsSeoPage = lazy(() => import("./pages/GeoVsSeoPage"));
const HowToRankInChatGptPage = lazy(() => import("./pages/HowToRankInChatGptPage"));
const AiCitationTrackingGuidePage = lazy(() => import("./pages/AiCitationTrackingGuidePage"));

// 404 catch-all — small page, lazy-loaded so it doesn't bloat the main bundle.
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

// Legal pages — required for GDPR/consumer compliance
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const RefundPolicyPage = lazy(() => import("./pages/RefundPolicyPage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));

// Dashboard — big chunk, only loaded after login
const DashboardLayout = lazy(() => import("./components/DashboardLayout"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ClientsPage = lazy(() => import("./pages/ClientsPage"));
const AdminUsersPage = lazy(() => import("./pages/AdminUsersPage"));
const AdminAffiliatesPage = lazy(() => import("./pages/AdminAffiliatesPage"));
const PSEODashboardPage = lazy(() => import("./pages/PSEODashboardPage"));
const CrawlStatsPage = lazy(() => import("./pages/CrawlStatsPage"));
const KeywordIntelPage = lazy(() => import("./pages/KeywordIntelPage"));
const ContentPipelinePage = lazy(() => import("./pages/ContentPipelinePage"));
const ContentDetailPage = lazy(() => import("./pages/ContentDetailPage")); // Tiptap editor — very heavy
const CMSBridgePage = lazy(() => import("./pages/CMSBridgePage"));
const OutreachPage = lazy(() => import("./pages/OutreachPage"));
const ReportingPage = lazy(() => import("./pages/ReportingPage"));
const ReportingDashboardPage = lazy(() => import("./pages/ReportingDashboardPage")); // Recharts — heavy
const BrandIntelPage = lazy(() => import("./pages/BrandIntelPage"));
const RedditAgentPage = lazy(() => import("./pages/RedditAgentPage"));
const AuditPage = lazy(() => import("./pages/AuditPage"));
const BrandMentionsPage = lazy(() => import("./pages/BrandMentionsPage"));
const ContentCalendarPage = lazy(() => import("./pages/ContentCalendarPage"));
const PseoPlannerPage = lazy(() => import("./pages/PseoPlannerPage"));
const ClientDashboard = lazy(() => import("./pages/ClientDashboard"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="w-6 h-6 border-2 border-[#FF2F2F] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Catch-all for unknown routes (`path="*"`).
//   - Authed users → their dashboard (admin or client) so a fat-fingered URL
//     drops them somewhere useful, not a 404.
//   - Anon visitors / crawlers → real NotFoundPage with `noindex, nofollow`
//     meta tag. Previously this redirected to "/", which made Google see
//     phantom URLs (spam backlinks like /item/5101580) as soft-404s or
//     duplicates of the homepage. The explicit noindex page tells Google
//     to drop these URLs entirely.
function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <NotFoundPage />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
}

// Small helper so each protected route doesn't need 3 levels of wrapping inline
function Protected({ children, requiredRole }) {
  return (
    <ProtectedRoute requiredRole={requiredRole}>
      <Suspense fallback={<PageLoader />}>
        <DashboardLayout>{children}</DashboardLayout>
      </Suspense>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <HelmetProvider>
    <AuthProvider>
      <ClientProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster
          position="top-right"
          richColors
          closeButton
          duration={4000}
          toastOptions={{
            style: { fontFamily: "Satoshi, system-ui, sans-serif", borderRadius: "0px" },
          }}
        />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public (landing + marketing) */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/home" element={<HomePage />} />

            {/* Blog — /blog is canonical, /blogs redirects for legacy links */}
            <Route path="/blog" element={<BlogsPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/blogs" element={<Navigate to="/blog" replace />} />
            <Route path="/blogs/:slug" element={<BlogRedirect />} />

            {/* Programmatic SEO use-case matrix — DB-driven, one component handles all slugs */}
            <Route path="/use-cases" element={<UseCasesIndexPage />} />
            <Route path="/use-cases/:slug" element={<UseCasePage />} />
            {/* Preview route — same component, serves drafts. Not in sitemap. */}
            <Route path="/preview/:slug" element={<UseCasePage />} />

            {/* Programmatic SEO hub pillars — Backlinko-style chapter guides at /hub/:slug.
                Bare /hub stays the ResourcesHubPage discovery page (defined further down). */}
            <Route path="/hub/:slug" element={<HubPage />} />
            <Route path="/preview-hub/:slug" element={<HubPage />} />

            <Route path="/affiliates" element={<AffiliatesPage />} />
            <Route path="/saas-affiliate-program" element={<SaasAffiliateProgramPage />} />
            <Route path="/affiliate-program" element={<Navigate to="/affiliates" replace />} />
            <Route path="/affiliate-dashboard" element={<AffiliateDashboardPage />} />
            <Route path="/affiliates/resources" element={<AffiliateResourcesPage />} />
            <Route path="/affiliates/terms" element={<AffiliateTermsPage />} />
            <Route path="/affiliate-program/for/seo-agencies" element={<AffiliateForSeoAgenciesPage />} />
            <Route path="/affiliate-program/for/content-creators" element={<AffiliateForContentCreatorsPage />} />
            <Route path="/affiliate-program/for/india" element={<AffiliateForIndiaPage />} />
            <Route path="/affiliate-program/earnings-calculator" element={<AffiliateEarningsCalculatorPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/integrations/chrome-extension" element={<ChromeExtensionPage />} />
            <Route path="/shopify" element={<ShopifyAppPage />} />
            <Route path="/shopify-app" element={<Navigate to="/shopify" replace />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />

            {/* Legal / compliance — required pages for GDPR + consumer rights */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            {/* Aliases — keep old paths working so any in-the-wild links don't break */}
            <Route path="/legal/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/legal/terms" element={<Navigate to="/terms-of-service" replace />} />
            <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
            <Route path="/legal/refund-policy" element={<Navigate to="/refund-policy" replace />} />
            <Route path="/legal/cookie-policy" element={<Navigate to="/cookie-policy" replace />} />

            {/* New marketing + auth pages */}
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/signup" element={<Navigate to="/register" replace />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/for-agencies" element={<ForAgenciesPage />} />
            <Route path="/geo-optimization" element={<GeoOptimizationPage />} />
            <Route path="/ai-citation-tracking" element={<AiCitationTrackingPage />} />
            {/* The old GEO checker returned a hash-based score, not a real scan. */}
            <Route path="/geo-checker" element={<Navigate to="/tools/ai-visibility-checker" replace />} />
            <Route path="/tools/ai-visibility-checker" element={<AiVisibilityCheckerPage />} />
            <Route path="/tools/schema-generator" element={<SchemaGeneratorPage />} />
            <Route path="/tools/meta-tag-analyzer" element={<MetaTagAnalyzerPage />} />
            <Route path="/tools/sitemap-analyzer" element={<SitemapAnalyzerPage />} />
            <Route path="/tools/ai-content-detector" element={<AiContentDetectorPage />} />

            {/* B2B sales pages */}
            <Route path="/agency-program" element={<AgencyProgramPage />} />
            <Route path="/enterprise" element={<EnterprisePage />} />

            {/* Resources hub — single-page discovery for all marketing surfaces */}
            <Route path="/resources" element={<ResourcesHubPage />} />
            <Route path="/tools" element={<ResourcesHubPage />} />
            <Route path="/hub" element={<ResourcesHubPage />} />

            {/* Changelog — public timeline of customer-facing updates */}
            <Route path="/changelog" element={<ChangelogPage />} />

            {/* /[tool]-alternative — bottom-funnel pages for AI writing tools */}
            <Route path="/jasper-alternative" element={<JasperAlternativePage />} />
            <Route path="/copy-ai-alternative" element={<CopyAiAlternativePage />} />
            <Route path="/writesonic-alternative" element={<WritesonicAlternativePage />} />
            <Route path="/outranking-alternative" element={<OutrankingAlternativePage />} />
            <Route path="/scalenut-alternative" element={<ScalenutAlternativePage />} />

            {/* Competitor alternatives + category pages */}
            <Route path="/ahrefs-alternative" element={<AhrefsAlternativePage />} />
            <Route path="/semrush-alternative" element={<SemrushAlternativePage />} />
            <Route path="/surfer-seo-alternative" element={<SurferSeoAlternativePage />} />
            <Route path="/clearscope-alternative" element={<ClearscopeAlternativePage />} />
            <Route path="/marketmuse-alternative" element={<MarketmuseAlternativePage />} />
            <Route path="/ai-seo-services" element={<AiSeoServicesPage />} />
            <Route path="/ai-seo-tools" element={<AiSeoToolsPage />} />
            <Route path="/keyword-clustering" element={<KeywordClusteringPage />} />
            <Route path="/seo-automation" element={<SeoAutomationPage />} />
            <Route path="/topical-authority" element={<TopicalAuthorityPage />} />
            <Route path="/search-intent-tool" element={<SearchIntentToolPage />} />

            {/* /vs comparison pages — head-to-head with competitors */}
            <Route path="/vs/surfer-seo" element={<VsSurferSeoPage />} />
            <Route path="/vs/frase" element={<VsFrasePage />} />
            <Route path="/vs/marketmuse" element={<VsMarketmusePage />} />
            <Route path="/vs/clearscope" element={<VsClearscopePage />} />
            <Route path="/vs/clearscope-vs-semrush" element={<VsClearscopeVsSemrushPage />} />
            <Route path="/vs/marketmuse-vs-clearscope" element={<VsMarketmuseVsClearscopePage />} />
            <Route path="/vs/neuronwriter" element={<VsNeuronwriterPage />} />
            <Route path="/vs/ahrefs" element={<VsAhrefsPage />} />
            <Route path="/vs/semrush" element={<VsSemrushPage />} />

            {/* Industry landing pages — programmatic SEO */}
            <Route path="/ai-seo-for-saas" element={<AiSeoForSaasPage />} />
            <Route path="/ai-seo-for-ecommerce" element={<AiSeoForEcommercePage />} />
            <Route path="/ai-seo-for-marketing-agencies" element={<AiSeoForMarketingAgenciesPage />} />
            <Route path="/ai-seo-for-real-estate" element={<AiSeoForRealEstatePage />} />
            <Route path="/ai-seo-for-healthcare" element={<AiSeoForHealthcarePage />} />
            <Route path="/ai-seo-for-law-firms" element={<AiSeoForLawFirmsPage />} />
            <Route path="/ai-seo-for-restaurants" element={<AiSeoForRestaurantsPage />} />
            <Route path="/ai-seo-for-dentists" element={<AiSeoForDentistsPage />} />
            <Route path="/ai-seo-for-coaches" element={<AiSeoForCoachesPage />} />
            <Route path="/ai-seo-for-b2b" element={<AiSeoForB2bPage />} />

            {/* SEO/GEO pillar + child pages */}
            <Route path="/seo-guide" element={<SeoGuidePage />} />
            <Route path="/geo-guide" element={<GeoGuidePage />} />
            <Route path="/what-is-search-intent" element={<WhatIsSearchIntentPage />} />
            <Route path="/search-intent-keyword-research" element={<SearchIntentKeywordResearchPage />} />
            <Route path="/geo-vs-seo" element={<GeoVsSeoPage />} />
            <Route path="/how-to-rank-in-chatgpt" element={<HowToRankInChatGptPage />} />
            <Route path="/ai-citation-tracking-guide" element={<AiCitationTrackingGuidePage />} />

            {/* Admin */}
            <Route path="/admin" element={<Protected requiredRole="admin"><AdminDashboard /></Protected>} />
            <Route path="/admin/clients" element={<Protected requiredRole="admin"><ClientsPage /></Protected>} />
            <Route path="/admin/users" element={<Protected requiredRole="admin"><AdminUsersPage /></Protected>} />
            <Route path="/admin/affiliates" element={<Protected requiredRole="admin"><AdminAffiliatesPage /></Protected>} />
            <Route path="/admin/pseo" element={<Protected requiredRole="admin"><PSEODashboardPage /></Protected>} />
            <Route path="/admin/crawl-stats" element={<Protected requiredRole="admin"><CrawlStatsPage /></Protected>} />

            {/* Shared (admin + client) */}
            <Route path="/brand-intel" element={<Protected><BrandIntelPage /></Protected>} />
            <Route path="/reddit" element={<Protected><RedditAgentPage /></Protected>} />
            <Route path="/audit" element={<Protected><AuditPage /></Protected>} />
            <Route path="/brand-mentions" element={<Protected><BrandMentionsPage /></Protected>} />
            <Route path="/ai-visibility" element={<Protected><AiVisibilityPage /></Protected>} />
            <Route path="/decay" element={<Protected><ContentDecayPage /></Protected>} />
            <Route path="/cannibalization" element={<Protected><CannibalizationPage /></Protected>} />
            <Route path="/keywords" element={<Protected><KeywordIntelPage /></Protected>} />
            <Route path="/content" element={<Protected><ContentPipelinePage /></Protected>} />
            <Route path="/content/:id" element={<Protected><ContentDetailPage /></Protected>} />
            <Route path="/cms" element={<Protected><CMSBridgePage /></Protected>} />
            <Route path="/outreach" element={<Protected><OutreachPage /></Protected>} />
            <Route path="/reporting" element={<Protected><ReportingDashboardPage /></Protected>} />
            <Route path="/reporting-legacy" element={<Protected><ReportingPage /></Protected>} />
            <Route path="/calendar" element={<Protected><ContentCalendarPage /></Protected>} />
            <Route path="/pseo" element={<Protected><PseoPlannerPage /></Protected>} />

            {/* Settings (Agency tier+) */}
            <Route path="/settings/whitelabel" element={<Protected><WhitelabelSettingsPage /></Protected>} />
            <Route path="/settings/api-keys" element={<Protected><ApiKeysPage /></Protected>} />

            {/* Client */}
            <Route path="/dashboard" element={<Protected><ClientDashboard /></Protected>} />

            {/* Fallback */}
            <Route path="*" element={<RootRedirect />} />
          </Routes>
        </Suspense>
        <GlobalChatWidget />
        <CookieConsentBanner />
      </BrowserRouter>
      </ClientProvider>
    </AuthProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

// Redirects legacy /blogs/:slug → /blog/:slug (301-like via Navigate replace).
function BlogRedirect() {
  const slug = window.location.pathname.split("/blogs/")[1] || "";
  return <Navigate to={`/blog/${slug}`} replace />;
}

export default App;
// build nudge 2026-05-26T12:22:44Z
